import { SignatureReact } from '@pepper/signature-react';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import ENVIRONMENT from '@/environments/environment';
import { AnalyticsOrigination, AnalyticsStepNumber } from '@/interfaces/AnalyticsInterface/AnalyticsInterface';
import { EDNIModalRoutes } from '@/interfaces/EDniModalInterface/EDniModalInterface';
import { EnvType } from '@/interfaces/EnviromentInterface/EnviromentInterface';
import { ErrorTypeCommon } from '@/interfaces/ErrorCodeSignature/ErrorCode';
import { activateCreditProposal } from '@/redux/eDniModal/eDniModalAPI';
import {
  getTimeResult, registerContractShown, registerLeadGenerated, registerOtpResend, registerPinError, registerPurchase, registerSignatureContinue,
} from '@/services/analytics';
import { fileOpenerService } from '@/services/fileOpener';
import TokenService from '@/services/token';
import { RootState } from '@/store';

import styles from './Signature.module.scss';
import CustomError from './CustomError/CustomError';

interface SignatureProps {
  nextStep: (arg0: EDNIModalRoutes) => void;
  handleLoading: (arg0: boolean) => void;
}

export default function Signature({
  nextStep,
  handleLoading,
}: SignatureProps) {
  const [successEnd, setSuccessEnd] = useState(false);
  const token = TokenService.getToken();
  const creditProposalResult = useSelector((state: RootState) => state.creditProposal.data);
  const simulation = useSelector(
    (state: RootState) => state.loanSimulation.data,
  );
  const [signatureError, setSignatureError] = useState<ErrorTypeCommon>();
  const [initTime] = useState<dayjs.Dayjs>(dayjs());

  const environment: EnvType = {
    type: ENVIRONMENT.ENV,
  };

  const handleSuccess = () => {
    handleLoading(true);
    setTimeout(() => {
      verifyApiCall(0, false);
    }, 3000);
  };

  const handleError = (error: any[] | any) => {
    if (
      error === 'expired'
      || error === 'unknown'
      || error === 'max_attempts'
    ) {
      nextStep(EDNIModalRoutes.UNKNOWN_ERROR);
    } else if (error === 'DRF0046') {
      nextStep(EDNIModalRoutes.END);
    } else if (error === 'ESG0017') {
      nextStep(EDNIModalRoutes.UNKNOWN_ERROR);
    } else if (error === 'ESG0014') {
      setSignatureError(ErrorTypeCommon.ESG0014);
    } else if (error === 'ESG0015') {
      setSignatureError(ErrorTypeCommon.ESG0015);
    } else {
      nextStep(EDNIModalRoutes.UNKNOWN_ERROR);
    }
    setTimeout(() => {
      setSignatureError(undefined);
    }, 3000);
  };

  const handleCancel = () => {
    nextStep(EDNIModalRoutes.UNKNOWN_ERROR);
  };

  const handleContractApp = (data: string) => {
    fileOpenerService.openContract(data);
  };

  const verifyApiCall = async (counter: number, isVerify: boolean) => {
    const count = counter;
    try {
      handleLoading(true);
      if (isVerify) {
        setSuccessEnd(false);
        handleLoading(false);
        nextStep(EDNIModalRoutes.END);
      } else {
        const loan = await activateCreditProposal(
          creditProposalResult!.credit_proposal_id,
        );
        if (loan && simulation) {
          const purchaseAnalytics = {
            currency: 'EUR',
            transaction_id: loan.loanId,
            value: simulation.financed_amount,
            items: {
              item_id: simulation?.commercial_offer_id,
              item_name: simulation?.commercial_offer_name,
            },
          };
          const leadAnalytics = {
            currency: 'EUR',
            value: simulation.financed_amount,
          };
          registerPurchase({ origin: AnalyticsOrigination.App, uuid: creditProposalResult?.credit_proposal_id || '' }, purchaseAnalytics);
          registerLeadGenerated({ origin: AnalyticsOrigination.App, uuid: creditProposalResult?.credit_proposal_id || '' }, leadAnalytics);
        }
        setSuccessEnd(false);
        nextStep(EDNIModalRoutes.END);
      }
    } catch (error: any) {
      const errorCode = error.response?.data.result.errors[0]?.code;
      if (
        error.result?.http_code === 422
        && errorCode === 'DRF0027'
        && count < 3
      ) {
        handleLoading(true);
        setTimeout(() => {
          verifyApiCall(count + 1, isVerify);
        }, 3000);
      } else {
        handleLoading(false);
        nextStep(EDNIModalRoutes.UNKNOWN_ERROR);
      }
    } finally {
      if (counter === 0) handleLoading(false);
    }
  };

  const handleSignatureStep = (step: string, thirdParty?: string) => {
    const third = thirdParty === 'PEP' ? 'PEPPER' : thirdParty === 'RUB' ? 'RUBRICAE' : thirdParty === 'CER' ? 'ECERTIC' : thirdParty === 'GAL' ? 'GALEON' : undefined;
    switch (step) {
      case 'SHOW_CONTRACT':
        registerContractShown({ origin: AnalyticsOrigination.App, uuid: creditProposalResult?.credit_proposal_id || '' }, third);
        break;
      case 'RESEND_OTP':
        registerOtpResend({ origin: AnalyticsOrigination.App, uuid: creditProposalResult?.credit_proposal_id || '' }, {
          time_elapsed: initTime ? getTimeResult(initTime) : 0,
          third: third || '',
        });
        break;
      case 'PIN_ERROR':
        registerPinError({ origin: AnalyticsOrigination.App, uuid: creditProposalResult?.credit_proposal_id || '' }, third);
        break;
      case 'CONTINUE_SIGNATURE':
        registerSignatureContinue({ origin: AnalyticsOrigination.App, uuid: creditProposalResult?.credit_proposal_id || '' }, {
          time_elapsed: initTime ? getTimeResult(initTime) : 0,
          step_number: AnalyticsStepNumber.Signature,
          third: third || '',
        });
        break;
    }
  };

  useEffect(() => {
    if (token && environment && !successEnd) {
      handleLoading(false);
    }
  }, []);

  return (
    <div className={styles.modalSignatureContainer}>
      <div className={styles.signatureWrapper}>
        {signatureError && (
          <CustomError errorCode={signatureError} />
        )}
        {token && environment && !successEnd && (
          <SignatureReact
            creditProposalID={creditProposalResult?.credit_proposal_id}
            currentEnvironment={environment}
            token={token}
            // generalPadding="20px 2.5rem"
            successEnd={handleSuccess}
            errorMessage={handleError}
            cancel={handleCancel}
            isApp
            sendContractData={handleContractApp}
            stepEvent={(step: string, thirdParty?: string) => handleSignatureStep(step, thirdParty)}
          />
        )}
      </div>
    </div>
  );
}
